import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiChevronDown, FiServer } from "react-icons/fi";
import Hls from "hls.js";
import { contentAPI } from "../api/endpoints";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import LoginPromptModal from "../components/LoginPromptModal";
import NativeAdPlayer from "../components/NativeAdPlayer";
import FluidVideoPlayer from "../components/FluidVideoPlayer";
import ImaAdManager from "../components/ImaAdManager";

const isDirectMediaUrl = (url = "") => {
  const lower = url.toLowerCase();
  return lower.includes(".m3u8") || lower.includes(".mp4") || lower.includes(".webm");
};

const getDeviceAdUrl = (link, isMobileView) => {
  if (!link) return "";

  const mobileAdUrl = link.mobile_ad_url || link.ad_url_mobile || link.mobileAdUrl;
  const desktopAdUrl = link.desktop_ad_url || link.ad_url_desktop || link.desktopAdUrl;

  if (isMobileView) {
    return mobileAdUrl || link.ad_url || desktopAdUrl || "";
  }

  return desktopAdUrl || mobileAdUrl || link.ad_url || "";
};

export default function Player() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const episodeId = searchParams.get("episode");
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  // Streaming links from API
  const [streamLinks, setStreamLinks] = useState([]);
  const [activeLink, setActiveLink] = useState(null);
  const [showServerList, setShowServerList] = useState(false);

  const [title, setTitle] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Raw Ad Native State
  const [adTagUrl, setAdTagUrl] = useState("");
  const [isAdPlaying, setIsAdPlaying] = useState(false);

  // TV show episode navigation
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [showEpisodeList, setShowEpisodeList] = useState(false);

  // Determine the stream type for the player
  const streamType = activeLink?.is_youtube
    ? "youtube"
    : activeLink?.is_embed
      ? "embed"
      : "hls";
  const useFluidPlayer = streamType === "hls" && activeLink?.link && adTagUrl && !isDirectMediaUrl(adTagUrl);

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (_) { }
      hlsRef.current = null;
    }
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = () => setIsMobileView(mediaQuery.matches);

    handleChange();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // ─── Load streaming links ────────────────────────────────────────────
  useEffect(() => {
    const loadStream = async () => {
      setLoading(true);
      setError(false);

      setAdTagUrl("");
      setIsAdPlaying(false);
      destroyHls();

      try {
        const apiType =
          episodeId
            ? "episode"
            : type === "tvshow" || type === "series"
              ? "episode"
              : "movie";
        const contentId = episodeId || id;

        const res = await contentAPI.getStreamingLinks(apiType, contentId);
        const links = Array.isArray(res.data)
          ? res.data
          : [res.data].filter(Boolean);

        if (!links.length) {
          setError(true);
          return;
        }

        setStreamLinks(links);

        // Prefer the first non-YouTube HD link, fallback to first
        const preferred = links.find((l) => !l.is_youtube && l.is_hd) || links[0];
        setActiveLink(preferred);

        const adUrl = getDeviceAdUrl(preferred, isMobileView);

        // If an ad URL exists, trigger the ad logic
        if (adUrl) {
          setAdTagUrl(adUrl);
          setIsAdPlaying(true);
        }
      } catch {
        setError(true);
        toast.error("Failed to load stream");
      } finally {
        setLoading(false);
      }
    };

    const loadTitle = async () => {
      try {
        const detailType =
          type === "tvshow" || type === "series" ? "series" : "movie";
        const res = await contentAPI.getDetail(detailType, id);
        setTitle(res.data?.title || res.data?.name || "");
      } catch {
        // Title is optional
      }
    };

    loadStream();
    loadTitle();

    if (type === "series" || type === "tvshow") {
      contentAPI
        .getSeasons(id)
        .then((seasonRes) => {
          const seasonList = seasonRes.data || [];
          if (!seasonList.length) return;
          setSeasons(seasonList);

          if (episodeId) {
            for (const s of seasonList) {
              contentAPI.getEpisodes(s.id).then((epRes) => {
                const eps = epRes.data || [];
                const found = eps.find((e) => String(e.id) === String(episodeId));
                if (found) {
                  setSelectedSeason(s);
                  setEpisodes(eps);
                  setCurrentEpisode(found);
                }
              });
            }
          } else {
            setSelectedSeason(seasonList[0]);
            contentAPI.getEpisodes(seasonList[0].id).then((epRes) => {
              setEpisodes(epRes.data || []);
            });
          }
        })
        .catch(() => { });
    }

    return () => destroyHls();
  }, [type, id, episodeId, isMobileView, destroyHls]);

  // ─── Main HLS Player Setup (Runs only when Ad is NOT playing) ────────
  useEffect(() => {
    // If the ad is currently playing, don't load the main movie yet!
    if (isAdPlaying || useFluidPlayer) return;

    const video = videoRef.current;
    if (!video || !activeLink || streamType !== "hls") return;

    const url = activeLink.link;
    if (!url) return;

    destroyHls();

    if (url.includes(".m3u8") && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => { });
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) setError(true);
      });
    } else {
      video.src = url;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => { });
      }, { once: true });
    }

    return () => destroyHls();
  }, [activeLink, streamType, isAdPlaying, useFluidPlayer, destroyHls]);

  // ─── Switch server ────────────────────────────────────────────────────
  const switchServer = useCallback(
    (link) => {
      destroyHls();
      setActiveLink(link);
      setShowServerList(false);
      setAdTagUrl("");
      setIsAdPlaying(false);

      const adUrl = getDeviceAdUrl(link, isMobileView);
      if (adUrl) {
        setAdTagUrl(adUrl);
        setIsAdPlaying(true);
      }
    },
    [destroyHls, isMobileView]
  );

  // ─── Episode navigation ───────────────────────────────────────────────
  const playEpisode = (ep) => {
    setCurrentEpisode(ep);
    setShowEpisodeList(false);
    navigate(`/player/tvshow/${id}?episode=${ep.id}`, { replace: true });
  };

  const handleSeasonSwitch = async (season) => {
    setSelectedSeason(season);
    try {
      const res = await contentAPI.getEpisodes(season.id);
      setEpisodes(res.data || []);
    } catch {
      setEpisodes([]);
    }
  };

  // ─── Builders ─────────────────────────────────────────────────────────
  const getYouTubeEmbedUrl = (link) => {
    const videoId = link.includes("youtu")
      ? link.split("v=")[1]?.split("&")[0] || link.split("/").pop()
      : link;
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
  };

  const getEmbedUrl = (link) => {
    if (!link) return "";
    let url = link;
    if (url.includes("admin.theslaynetwork.tv") || url.includes("/api/")) {
      const token = localStorage.getItem("auth_token");
      if (token && !url.includes("token=")) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}token=${token}`;
      }
    }
    return url;
  };

  // ─── Ad Complete Callback ─────────────────────────────────────────────
  const handleAdComplete = useCallback(() => {
    setIsAdPlaying(false);
    // This automatically unmounts ad players, triggers the main HLS useEffect,
    // and starts the actual movie.
  }, []);

  const handleFluidAdFinished = useCallback(() => {
    setAdTagUrl("");
    setIsAdPlaying(false);
  }, []);

  // ─── Auth guard ──────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black">
        <LoginPromptModal show onClose={() => navigate(-1)} />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-dark-900/80 backdrop-blur-sm z-20">
        <button
          onClick={() => {
            destroyHls();
            navigate(-1);
          }}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
        >
          <FiArrowLeft size={18} /> Back
        </button>
        <h2 className="text-sm font-medium text-white truncate max-w-[40%] text-center">
          {title}
          {currentEpisode &&
            ` — ${currentEpisode.name || `Ep ${currentEpisode.episode_number}`}`}
        </h2>
        <div className="flex items-center gap-2">
          {/* Server switcher */}
          {streamLinks.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setShowServerList(!showServerList)}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-sm px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                disabled={isAdPlaying} // Disable server switch during ad so logic cleanly fires
              >
                <FiServer size={14} />
                {activeLink?.server || "Server"}
              </button>
              {showServerList && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-dark-700 border border-dark-400 rounded-xl shadow-2xl overflow-hidden z-40">
                  {streamLinks.map((link, i) => (
                    <button
                      key={i}
                      onClick={() => switchServer(link)}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between ${activeLink === link
                        ? "bg-primary/20 text-primary"
                        : "text-gray-300 hover:bg-white/5"
                        }`}
                    >
                      <span>{link.server || `Server ${i + 1}`}</span>
                      <span className="flex items-center gap-1.5 text-xs text-gray-500">
                        {link.is_hd && <span className="text-yellow-400 font-semibold">HD</span>}
                        {link.is_youtube && <span>YT</span>}
                        {link.is_embed && <span>Embed</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Episodes button */}
          {(type === "series" || type === "tvshow") && episodes.length > 0 && (
            <button
              onClick={() => setShowEpisodeList(!showEpisodeList)}
              className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
              disabled={isAdPlaying}
            >
              Episodes <FiChevronDown size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative bg-black">
        <div className="w-full h-full max-h-[calc(100vh-56px)] flex items-center justify-center relative bg-dark-900 mx-auto aspect-video max-w-[177.77vh]">
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 animate-pulse">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500 font-medium">Connecting to stream...</p>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-gray-400 bg-black">
              <p className="text-lg font-medium mb-2">Playback Error</p>
              <p className="text-sm">Unable to load this content. Please try again later.</p>
              <button onClick={() => navigate(-1)} className="mt-4 btn-play-gradient-sm">
                Go Back
              </button>
            </div>
          ) : streamType === "youtube" ? (
            <iframe
              src={getYouTubeEmbedUrl(activeLink.link)}
              title={title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
            />
          ) : streamType === "embed" ? (
            <iframe
              src={getEmbedUrl(activeLink.link)}
              title={title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          ) : useFluidPlayer ? (
            <FluidVideoPlayer
              key={`${activeLink.link}-${adTagUrl}`}
              videoUrl={activeLink.link}
              adUrl={adTagUrl}
              onAdFinished={handleFluidAdFinished}
            />
          ) : (
            /* Native HLS Main Movie Player */
            <>
              <video
                ref={videoRef}
                controls={!isAdPlaying} // Hide core controls while ad wrapper holds the screen
                playsInline
                className={`absolute inset-0 w-full h-full object-contain bg-black ${(isAdPlaying && isDirectMediaUrl(adTagUrl)) ? 'opacity-0 pointer-events-none' : 'opacity-100'
                  }`}
              />

              {/* Native Pre-Roll Ad Override for raw media files */}
              {isAdPlaying && adTagUrl && isDirectMediaUrl(adTagUrl) && (
                <NativeAdPlayer
                  adUrl={adTagUrl}
                  onComplete={handleAdComplete}
                />
              )}
            </>
          )}
        </div>

        {/* Episode Side Panel */}
        {showEpisodeList && !isAdPlaying && (
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-dark-900/95 backdrop-blur-md border-l border-dark-400 overflow-y-auto z-30">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Episodes</h3>

              {seasons.length > 1 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {seasons.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleSeasonSwitch(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${selectedSeason?.id === s.id
                        ? "bg-primary text-white"
                        : "bg-dark-700 text-gray-400 border border-dark-400"
                        }`}
                    >
                      {s.name || `S${s.season_number}`}
                    </button>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {episodes.map((ep, i) => (
                  <button
                    key={ep.id}
                    onClick={() => playEpisode(ep)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${currentEpisode?.id === ep.id
                      ? "bg-primary/20 border border-primary/40"
                      : "bg-dark-700 hover:bg-dark-600"
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-gray-600 w-6">
                        {ep.episode_number || i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {ep.name || `Episode ${ep.episode_number || i + 1}`}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
}
